import { blue, green, red } from "@material-ui/core/colors";
import _ from "lodash";
import * as Plotly from "plotly.js-dist";
import { CustomerGroupMeta } from "src/lib/models/CustomerGroup";

import { numberFormat } from "../../utilities";

const cropLabelsNBAChart = (label: string) => {
  const cropLength = 60;
  return label.length > cropLength ? label.slice(0, cropLength) + "..." : label;
};

const getFullXLabelForTooltip = (
  meta: CustomerGroupMeta,
  tickLabel: string
) => {
  if (tickLabel.slice(-3) === "...") {
    const fullLabels = meta.nextBestActivityTopTen.map((nba) => nba.nba_total);
    const foundLabel = fullLabels.find((label) =>
      label.includes(tickLabel.slice(0, -3))
    );
    return foundLabel ?? tickLabel;
  }
  return tickLabel;
};

const customToolTip = (e: any, meta: CustomerGroupMeta) => {
  const hoverLabel: any = e;
  const name0 =
    hoverLabel.points[0] != null ? hoverLabel.points[0].fullData.name : "";
  let name1 = "";
  let xlabel;

  switch (name0.substring(0, 3)) {
    case "CLV":
      name1 = "CLV (Auswahl)";
      break;
    case "Ber":
      name1 = "Beratungsfokus (Auswahl)";
      xlabel = `<b>${getFullXLabelForTooltip(
        meta,
        hoverLabel.points[0].label
      )}</b><br>`;
      break;
    default:
      name1 = "Churn Score (Auswahl)";
  }

  const point0 = hoverLabel.points[0] != null ? hoverLabel.points[0].y : 0;
  const point1 = hoverLabel.points[1] != null ? hoverLabel.points[1].y : 0;
  hoverLabel.points[0].fullData.text =
    (xlabel ?? "") +
    `${name0}: ${numberFormat(Number(point0), true)} %` +
    `<br>${name1}: ${numberFormat(Number(point1), true)} %`;
};

const getHighestClvAmount = (meta: CustomerGroupMeta) => {
  const result = _(meta.amountAllClv)
    .groupBy("clv")
    .map((v) => v)
    .value()
    .sort();

  let sum = 0;
  for (const i of result) {
    sum += i.length;
  }

  if (result != null) {
    return (result.pop()!.length / sum) * 100 + 10;
  } else {
    return 0;
  }
};

const getHighestChurnAmount = (meta: CustomerGroupMeta) => {
  const result = _(meta.amountAllChurn)
    .groupBy("churn")
    .map((v) => v)
    .value()
    .sort();

  let sum = 0;
  for (const i of result) {
    sum += i.length;
  }

  if (result != null) {
    return (result.pop()!.length / sum) * 100 + 10;
  } else {
    return 0;
  }
};

export const createNextBestActivityBarChart = (
  meta: CustomerGroupMeta
): void => {
  const data = [
    {
      hoverinfo: "text",
      hoverlabel: {
        align: "left",
        bgcolor: "white",
        bordercolor: "gray",
        font: {
          color: "black",
          family: "Arial, sans-serif",
          size: 14,
        },
      },
      marker: {
        color: blue[700],
      },
      name: "Beratungsfokus (Gesamt)",
      opacity: 0.3,
      text: "",
      type: "bar",
      x: meta.nextBestActivityTopTen.map((nba) =>
        cropLabelsNBAChart(nba.nba_total)
      ),
      y: meta.nextBestActivityTopTen.map(
        (nba) => (nba.amount_total / meta.totalCount) * 100
      ),
    },
    {
      hoverinfo: "none",
      marker: {
        color: blue[700],
      },
      name: "Beratungsfokus (Auswahl)",
      type: "bar",
      x: meta.nextBestActivityTopTen.map((nba) =>
        cropLabelsNBAChart(nba.nba_total)
      ),
      y: meta.nextBestActivityTopTen.map(
        (nba) => (nba.amount_selection / meta.selectedCount) * 100
      ),
    },
  ];

  const layout = {
    barmode: "group",
    height: 600,
    legend: { orientation: "h", xanchor: "center", x: 0.5, y: 1.2 },
    margin: {
      b: 300,
    },
    xaxis: {
      dtick: 1,
      fixedrange: true,
      tick0: 0,
      tickangle: "-45",
      tickmode: "linear",
      automargin: true,
    },
    yaxis: {
      fixedrange: true,
      title: "Kundenanteil in %",
    },
  };

  const config = { displayModeBar: false, responsive: true };

  Plotly.newPlot("nextBestActivityBarChart", data, layout, config);

  const nextBestActivityBarChartEl = document.getElementById(
    "nextBestActivityBarChart"
  );
  if (nextBestActivityBarChartEl != null) {
    (nextBestActivityBarChartEl as any)!.on("plotly_hover", (e: any) =>
      customToolTip(e, meta)
    );
  }
};

export const createChurnHistogram = (meta: CustomerGroupMeta): void => {
  const data = [
    {
      histnorm: "percent",
      hoverinfo: "text",
      hoverlabel: {
        align: "left",
        bgcolor: "white",
        bordercolor: "gray",
        font: {
          color: "black",
          family: "Arial, sans-serif",
          size: 14,
        },
      },
      marker: {
        color: red[700],
      },
      name: "Churn Score (Gesamt)",
      opacity: 0.3,
      text: "",
      type: "histogram",
      x: meta.amountAllChurn
        .filter((v) => v.churn !== null && v.churn !== undefined)
        .map((v) => Math.round(v.churn)),
    },
    {
      histnorm: "percent",
      hoverinfo: "none",
      marker: {
        color: red[700],
      },
      visible: meta.amountSelChurn.length > 0,
      name: "Churn Score (Auswahl)",
      type: "histogram",
      x: meta.amountSelChurn
        .filter((v) => v.churn !== null && v.churn !== undefined)
        .map((v) => Math.round(v.churn)),
    },
    {
      hoverinfo: "x",
      hoverlabel: {
        align: "left",
        bgcolor: "white",
        bordercolor: "gray",
        font: {
          color: "black",
          family: "Arial, sans-serif",
          size: 14,
        },
      },
      hovertemplate: `Durchschnittlicher Churn Score (Gesamt): ${numberFormat(
        meta.avgChurnTotal,
        true
      )}<extra></extra>`,
      line: {
        color: "black",
        dash: "solid",
        shape: "linear",
      },
      name: "Durchschnittlicher Churn Score (Gesamt)",
      type: "scatter",
      x: [meta.avgChurnTotal, meta.avgChurnTotal],
      y: [0, getHighestChurnAmount(meta)],
    },
    {
      hoverinfo: "x",
      hoverlabel: {
        align: "left",
        bgcolor: "white",
        bordercolor: "gray",
        font: {
          color: "black",
          family: "Arial, sans-serif",
          size: 14,
        },
      },
      hovertemplate: `Durchschnittlicher Churn Score (Auswahl): ${numberFormat(
        meta.avgChurn,
        true
      )}<extra></extra>`,
      line: {
        color: "black",
        dash: "dot",
        shape: "linear",
      },
      name: "Durchschnittlicher Churn Score (Auswahl)",
      type: "scatter",
      x: [meta.avgChurn, meta.avgChurn],
      y: [0, getHighestChurnAmount(meta)],
    },
  ];

  const layout = {
    barmode: "group",
    bargap: meta.amountSelChurn.length === 0 ? 0.55 : 0.15,
    legend: { orientation: "h", xanchor: "center", x: 0.5, y: -0.5 },
    title: "Durchschnitt Churn Score",
    xaxis: {
      dtick: 1,
      fixedrange: true,
      tick0: 0,
      tickmode: "linear",
      title: "Churn Score",
    },
    yaxis: {
      fixedrange: true,
      title: "Kundenanteil in %",
    },
  };

  const config = { displayModeBar: false, responsive: true };

  Plotly.newPlot("churnHistogram", data, layout, config);

  const churnHistogramEl = document.getElementById("churnHistogram");
  if (churnHistogramEl != null) {
    (churnHistogramEl as any)!.on("plotly_hover", (e: any) =>
      customToolTip(e, meta)
    );
  }
};

export const createClvHistogram = (meta: CustomerGroupMeta): void => {
  const data = [
    {
      histnorm: "percent",
      hoverinfo: "text",
      hoverlabel: {
        align: "left",
        bgcolor: "white",
        bordercolor: "gray",
        font: {
          color: "black",
          family: "Arial, sans-serif",
          size: 14,
        },
      },
      marker: {
        color: green[700],
      },
      name: "CLV (Gesamt)",
      opacity: 0.3,
      text: "",
      type: "histogram",
      x: meta.amountAllClv
        .filter((v) => v.clv !== null && v.clv !== undefined)
        .map((v) => Math.round(v.clv)),
    },
    {
      histnorm: "percent",
      hoverinfo: "none",
      marker: {
        color: green[700],
      },
      visible: meta.amountSelClv.length > 0,
      name: "CLV (Auswahl)",
      type: "histogram",
      x: meta.amountSelClv
        .filter((v) => v.clv !== null && v.clv !== undefined)
        .map((v) => Math.round(v.clv)),
    },
    {
      hoverinfo: "x",
      hoverlabel: {
        align: "left",
        bgcolor: "white",
        bordercolor: "gray",
        font: {
          color: "black",
          family: "Arial, sans-serif",
          size: 14,
        },
      },
      hovertemplate: `Durchschnittlicher CLV (Gesamt): ${numberFormat(
        meta.avgClvTotal,
        true
      )}<extra></extra>`,
      line: {
        color: "black",
        dash: "solid",
        shape: "linear",
      },
      name: "Durchschnittlicher CLV (Gesamt)",
      type: "scatter",
      x: [meta.avgClvTotal, meta.avgClvTotal],
      y: [0, getHighestClvAmount(meta)],
    },
    {
      hoverinfo: "x",
      hoverlabel: {
        align: "left",
        bgcolor: "white",
        bordercolor: "gray",
        font: {
          color: "black",
          family: "Arial, sans-serif",
          size: 14,
        },
      },
      hovertemplate: `Durchschnittlicher CLV (Auswahl): ${numberFormat(
        meta.avgClv,
        true
      )}<extra></extra>`,
      line: {
        color: "black",
        dash: "dot",
        shape: "linear",
      },
      name: "Durchschnittlicher CLV (Auswahl)",
      type: "scatter",
      x: [meta.avgClv, meta.avgClv],
      y: [0, getHighestClvAmount(meta)],
    },
  ];

  const layout = {
    barmode: "group",
    bargap: meta.amountSelClv.length === 0 ? 0.55 : 0.15,
    legend: { orientation: "h", xanchor: "center", x: 0.5, y: -0.5 },
    title: "Durchschnitt CLV",
    xaxis: {
      dtick: 1,
      fixedrange: true,
      tick0: 0,
      tickmode: "linear",
      title: "CLV",
    },
    yaxis: {
      fixedrange: true,
      title: "Kundenanteil in %",
    },
  };

  const config = { displayModeBar: false, responsive: true };

  Plotly.newPlot("clvHistogram", data, layout, config);

  const clvHistogramEl = document.getElementById("clvHistogram");

  if (clvHistogramEl != null) {
    (clvHistogramEl as any)!.on("plotly_hover", (e: any) =>
      customToolTip(e, meta)
    );
  }
};



// WEBPACK FOOTER //
// ./src/components/CustomerGroups/CustomerGroupResultCharts.tsx